import React, { useState, cloneElement, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface ActionIconProps {
  icon: React.ReactElement;
  label: string;
  onClick?: () => void;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  active?: boolean;
}

export default function ActionIcon({
  icon,
  label,
  onClick,
  className = '',
  size = 'sm',
  active = false
}: ActionIconProps) {
  const { theme } = useTheme();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const sizeStyles = {
    xs: 'p-1 text-[10px]',
    sm: 'p-1.5 text-[11px]',
    md: 'p-2 text-xs',
    lg: 'p-2.5 text-sm'
  };

  const iconSizes = {
    xs: 'h-3.5 w-3.5',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  // Clone and resize the icon
  const resizedIcon = cloneElement(icon, {
    className: `${iconSizes[size]} ${icon.props.className || ''}`
  });

  useEffect(() => {
    const updateTooltipPosition = () => {
      if (!buttonRef.current || !tooltipRef.current || !showTooltip) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate initial position (centered above the button)
      let top = buttonRect.top - tooltipRect.height - 8;
      let left = buttonRect.left + (buttonRect.width - tooltipRect.width) / 2;

      // Adjust horizontal position if tooltip would overflow viewport
      if (left < 8) {
        left = 8; // Minimum 8px from left edge
      } else if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8; // Minimum 8px from right edge
      }

      // If tooltip would overflow top of viewport, show it below the button instead
      if (top < 8) {
        top = buttonRect.bottom + 8;
      }

      setTooltipPosition({ top, left });
    };

    // Update position when tooltip becomes visible
    if (showTooltip) {
      updateTooltipPosition();
    }

    // Update position on scroll and resize
    window.addEventListener('scroll', updateTooltipPosition);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [showTooltip]);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          group flex items-center justify-center rounded-lg transition-all duration-200
          ${sizeStyles[size]}
          ${active
            ? theme === 'dark'
              ? 'bg-gray-800 text-white'
              : 'bg-white text-gray-900 shadow-sm'
            : theme === 'dark'
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              : 'text-gray-600 hover:text-gray-900 hover:bg-white hover:shadow-sm'
          }
        `}
      >
        {resizedIcon}
      </button>

      {showTooltip && label && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
          className={`
            px-2 py-1 rounded text-[10px] whitespace-nowrap pointer-events-none
            ${theme === 'dark'
              ? 'bg-gray-800 text-gray-200'
              : 'bg-gray-900 text-white'
            }
            z-50 shadow-lg
          `}
        >
          {label}
          <div className={`
            absolute -bottom-1 left-1/2 -translate-x-1/2
            w-2 h-2 rotate-45
            ${theme === 'dark'
              ? 'bg-gray-800'
              : 'bg-gray-900'
            }
          `} />
        </div>
      )}
    </div>
  );
}